import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
  BarChart, Bar,
} from 'recharts';
import { costsApi } from '../api/costs';
import Card from './Card';
import LoadingSpinner from './LoadingSpinner';

const COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#8b5cf6', '#ec4899', '#14b8a6'];

export default function AnalyticsTab() {
  const [period, setPeriod] = useState(30);

  const { data: trends, isLoading: trendsLoading } = useQuery({
    queryKey: ['costs-trends', period],
    queryFn: () => costsApi.getTrends(period),
  });

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['costs-summary'],
    queryFn: () => costsApi.getSummary(),
  });

  // Aggregate trends by date for line chart
  const dailyCosts = useMemo(() => {
    if (!trends?.data?.length) return [];
    const map = new Map<string, { date: string; cost: number; operations: number }>();
    for (const row of trends.data) {
      const d = String(row.date).slice(0, 10);
      const existing = map.get(d);
      if (existing) {
        existing.cost += parseFloat(String(row.cost)) || 0;
        existing.operations += parseInt(String(row.operations)) || 0;
      } else {
        map.set(d, { date: d, cost: parseFloat(String(row.cost)) || 0, operations: parseInt(String(row.operations)) || 0 });
      }
    }
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [trends]);

  // Model distribution for pie chart
  const modelData = useMemo(() => {
    if (!summary?.by_model?.length) return [];
    return summary.by_model.map(m => ({
      name: m.model_name || 'unknown',
      value: parseFloat(String(m.total_cost_usd)) || 0,
      operations: parseInt(String(m.operations_count)) || 0,
    }));
  }, [summary]);

  // Top series for bar chart
  const topSeries = useMemo(() => {
    if (!summary?.by_series?.length) return [];
    return summary.by_series
      .filter(s => s.total_cost_usd > 0)
      .sort((a, b) => b.total_cost_usd - a.total_cost_usd)
      .slice(0, 10)
      .map(s => ({
        name: (s.series_title || 'Untitled').slice(0, 20),
        cost: s.total_cost_usd,
        operations: s.operations_count,
      }));
  }, [summary]);

  const overall = summary?.overall;
  const totalCost = parseFloat(String(overall?.total_cost_usd ?? 0));
  const totalOps = parseInt(String(overall?.total_operations ?? 0));
  const totalChapters = parseInt(String(overall?.total_chapters ?? 0));
  const avgCostPerChapter = totalChapters > 0 ? totalCost / totalChapters : 0;

  if (trendsLoading || summaryLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <div className="text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">Общие затраты</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">${totalCost.toFixed(4)}</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">Операций</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalOps.toLocaleString()}</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">Глав обработано</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalChapters}</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">Ср. стоимость главы</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">${avgCostPerChapter.toFixed(4)}</p>
          </div>
        </Card>
      </div>

      {/* Cost trends line chart */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Затраты по дням</h3>
          <div className="flex gap-2">
            {[7, 30, 90].map(d => (
              <button
                key={d}
                onClick={() => setPeriod(d)}
                className={`px-3 py-1 text-sm rounded-md ${
                  period === d
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                    : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>
        {dailyCosts.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dailyCosts}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#9ca3af" tickFormatter={v => v.slice(5)} />
              <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" tickFormatter={v => `$${v}`} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                labelStyle={{ color: '#d1d5db' }}
                itemStyle={{ color: '#d1d5db' }}
                formatter={(value: number) => [`$${value.toFixed(4)}`, 'Затраты']}
              />
              <Line type="monotone" dataKey="cost" stroke="#6366f1" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-center py-8 text-gray-500 dark:text-gray-400">Нет данных за выбранный период</p>
        )}
      </Card>

      {/* Model distribution + Top series */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Pie chart: models */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Распределение по моделям</h3>
          {modelData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={modelData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  dataKey="value"
                  nameKey="name"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={false}
                >
                  {modelData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                  itemStyle={{ color: '#d1d5db' }}
                  formatter={(value: number) => `$${value.toFixed(4)}`}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center py-8 text-gray-500 dark:text-gray-400">Нет данных</p>
          )}
        </Card>

        {/* Bar chart: top series */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Топ серий по затратам</h3>
          {topSeries.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topSeries} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                <XAxis type="number" tick={{ fontSize: 12 }} stroke="#9ca3af" tickFormatter={v => `$${v}`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} stroke="#9ca3af" width={100} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                  itemStyle={{ color: '#d1d5db' }}
                  formatter={(value: number) => [`$${value.toFixed(4)}`, 'Затраты']}
                />
                <Bar dataKey="cost" fill="#6366f1" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center py-8 text-gray-500 dark:text-gray-400">Нет данных</p>
          )}
        </Card>
      </div>
    </div>
  );
}
